import { useQuery } from "@tanstack/react-query";
import { useBookingStateContext } from "hooks/use-booking-state";
import moment from "moment-timezone";
import { Moment } from "moment/moment";
import qs from "qs";
import { useParams } from "react-router-dom";
import { getSearchParams } from "search-params";
import { AvailableTimesUnit } from "types/available-times";
import { Experience } from "types/experience";
import { WebRuleBookingType } from "types/web-rule";
import { dateToString } from "utils/moment-utils";
import { get } from "./axios-base";
import { useWebBooking } from "api/api-hooks/use-web-booking";
import {
    filterAndSortAvailableTimes,
    filterAndSortExperiences,
} from "api/api-hooks/available-times-utils";

interface AvailableTimesParams {
    date: string;
    unitId: string;
    sectionId?: number[];
    tag?: string;
    invite?: string;
}

function useAvailableTimes(
    date: Moment | undefined,
    sectionIdsOverride: number[] | undefined = undefined,
    unitIdOverride: string | undefined = undefined
) {
    const { unitId } = useParams();
    const { sectionIds, tag, invite } = getSearchParams();
    const { guests: stateGuests } = useBookingStateContext();
    const { data: booking } = useWebBooking();
    const guests = stateGuests ?? booking?.guests ?? 0;

    const dateString = dateToString(date);

    const params: AvailableTimesParams = {
        date: dateString,
        unitId: unitIdOverride || unitId || "",
    };

    // Override wins over the URL param, matching unitId above: a caller asking for a specific
    // section means it, and a stale sectionIds left in the URL must not redirect the request.
    if (sectionIdsOverride || sectionIds) {
        params.sectionId = sectionIdsOverride || sectionIds;
    }

    if (tag) {
        params.tag = tag;
    }

    if (invite) {
        params.invite = invite;
    }

    return useQuery({
        // Section and unit are part of the request, so they belong in the key: without them two
        // callers asking for different sections on the same date share one cache entry and the
        // second is served the first one's payload. Both stay after the date so the prefix
        // invalidations (["availableTimes"] and ["availableTimes", date]) keep matching.
        queryKey: [
            "availableTimes",
            date?.format("YYYY-MM-DD"),
            guests,
            params.unitId,
            params.sectionId ?? null,
            // The response depends on the invitation (VIP) link, so a normal visit's cache entry
            // must not answer a VIP request, or the other way round.
            params.invite ?? null,
        ],
        queryFn: async () => {
            const response = await get(`/WebBooking/availableTimes`, {
                params,
                // Typed as the params object itself: axios 1.19 infers the serializer's
                // parameter from the sibling `params` property.
                paramsSerializer: (params: AvailableTimesParams) => {
                    return qs.stringify(params, { arrayFormat: "repeat" });
                },
                transformResponse: (data: unknown) => {
                    const units = (JSON.parse(data as string) as AvailableTimesUnit[]).map(
                        (unit) => new AvailableTimesUnit(unit)
                    );
                    const now = moment();
                    units.forEach((unit) => {
                        const { name } = unit;

                        unit.sections.forEach((section) => {
                            section.timeSets.forEach((timeSet) => {
                                const { title } = timeSet;
                                timeSet.sectionId = section.id;
                                timeSet.times.forEach((time) => {
                                    time.name = name;
                                    time.title = title;
                                    const { recoupTime } = timeSet;
                                    time.recoupTime = recoupTime;

                                    time.ruleId = timeSet.id;
                                    time.availableUntil = moment(
                                        time.availableUntil,
                                        "YYYY-MM-DDTHH:mm:ss"
                                    );
                                    time.start = moment(time.start, "YYYY-MM-DDTHH:mm:ss");
                                    time.end = moment(time.end, "YYYY-MM-DDTHH:mm:ss");

                                    const isTimeExpired = now.isAfter(time.availableUntil);
                                    if (isTimeExpired) {
                                        time.timeExpired = true;
                                    }

                                    const isSeatsUnavailable =
                                        !time.availableSeats.includes(guests);
                                    if (isSeatsUnavailable) {
                                        time.unavailableForGroupSize = true;
                                    }

                                    const isWaitListUnavailable =
                                        !time.waitListSeats?.includes(guests);
                                    if (isWaitListUnavailable) {
                                        time.waitListUnavailableForGroupSize = true;
                                    }
                                });
                            });
                        });

                        unit.experiences.forEach((experience) => {
                            experience.activities.forEach((activity) => {
                                activity.timeSets.forEach((timeSet) => {
                                    const { title } = timeSet;
                                    timeSet.sectionId = activity.sectionId;
                                    timeSet.times.forEach((time) => {
                                        time.name = name;
                                        time.title = title;
                                        const { recoupTime } = timeSet;
                                        time.recoupTime = recoupTime;

                                        time.ruleId = timeSet.id;
                                        time.availableUntil = moment(
                                            time.availableUntil,
                                            "YYYY-MM-DDTHH:mm:ss"
                                        );
                                        time.start = moment(time.start, "YYYY-MM-DDTHH:mm:ss");
                                        time.end = moment(time.end, "YYYY-MM-DDTHH:mm:ss");

                                        const isTimeExpired = now.isAfter(time.availableUntil);
                                        if (isTimeExpired) {
                                            time.timeExpired = true;
                                        }

                                        const isSeatsUnavailable =
                                            !time.availableSeats.includes(guests);
                                        if (isSeatsUnavailable) {
                                            time.unavailableForGroupSize = true;
                                        }

                                        const isWaitListUnavailable =
                                            !time.waitListSeats?.includes(guests);
                                        if (isWaitListUnavailable) {
                                            time.waitListUnavailableForGroupSize = true;
                                        }
                                    });
                                });
                            });
                        });
                    });

                    return units;
                },
            });
            return response.data as AvailableTimesUnit[];
        },
        enabled: !!date && (!!unitId || !!unitIdOverride),
        staleTime: 3 * 60 * 1000, // 3 minutes to keep the data fresh
    });
}

function useFilteredAvailableTimes(
    date: Moment | undefined,
    sectionIdsOverride: number[] | undefined = undefined,
    unitIdOverride: string | undefined = undefined
) {
    const {
        data: availableTimesData,
        isLoading,
        isError,
    } = useAvailableTimes(date, sectionIdsOverride, unitIdOverride);
    const { isBookingRequest, guests: stateGuests } = useBookingStateContext();
    const { data: booking } = useWebBooking();
    const guests = stateGuests ?? booking?.guests ?? 0;

    if (!availableTimesData || isLoading || isError || availableTimesData.length === 0) return [];

    const targetBookingType = isBookingRequest
        ? WebRuleBookingType.Request
        : WebRuleBookingType.Booking;

    const availableTimesCopy = availableTimesData[0].clone();
    return filterAndSortAvailableTimes(
        availableTimesCopy,
        targetBookingType,
        guests,
        isBookingRequest
    );
}

/**
 * Filters and sorts experiences from available times data
 * An experience is only available if ALL its activities are available
 * @param date The date to get experiences for
 * @param sectionIdsOverride Optional array of section IDs to filter by
 * @param unitIdOverride Optional unit ID override
 * @returns Filtered and sorted array of experiences
 */
function useFilteredExperiences(
    date: Moment | undefined,
    sectionIdsOverride: number[] | undefined = undefined,
    unitIdOverride: string | undefined = undefined
): Experience[] {
    const {
        data: availableTimesData,
        isLoading,
        isError,
    } = useAvailableTimes(date, sectionIdsOverride, unitIdOverride);
    const { isBookingRequest, guests: stateGuests } = useBookingStateContext();
    const { data: booking } = useWebBooking();
    const { sectionIds } = getSearchParams();
    const guests = stateGuests ?? booking?.guests ?? 0;

    if (!availableTimesData || isLoading || isError) return [];

    const experiencesCopy = availableTimesData[0].experiences.map((exp) => exp.clone());
    const sectionFilter = sectionIdsOverride ?? sectionIds;

    return filterAndSortExperiences(
        experiencesCopy,
        isBookingRequest ? WebRuleBookingType.Request : WebRuleBookingType.Experience,
        guests,
        isBookingRequest,
        sectionFilter
    );
}

export { useAvailableTimes, useFilteredAvailableTimes, useFilteredExperiences };
